import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Markdown, Spacer, Text, useTheme } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';

import WarningGif from 'components/WarningGif';
import './styles.scss';

const AutomationActivationConfirmModal = memo(({ name, onRef }) => {
    const theme = useTheme();
    const callbacksRef = useRef({ onConfirm: null, onCancel: null });
    const [open, setOpen] = useState(false);
    const [showSaveInactive, setShowSaveInactive] = useState(true);

    useEffect(() => {
        onRef &&
            onRef({
                open({ onConfirm, onCancel, showSaveInactive: showInactive = true } = {}) {
                    callbacksRef.current = { onConfirm, onCancel };
                    setShowSaveInactive(showInactive);
                    setOpen(true);
                },
            });
    }, [onRef]);

    const handleClose = useCallback(() => {
        setOpen(false);
        callbacksRef.current = { onConfirm: null, onCancel: null };
    }, []);

    const handleConfirm = useCallback(() => {
        setOpen(false);
        callbacksRef.current?.onConfirm?.();
        callbacksRef.current = { onConfirm: null, onCancel: null };
    }, []);

    const handleCancel = useCallback(() => {
        setOpen(false);
        callbacksRef.current?.onCancel?.();
        callbacksRef.current = { onConfirm: null, onCancel: null };
    }, []);

    return (
        <Modal
            overlayClassName="fm-automation-activation-confirm-modal"
            isOpen={open}
            onRequestClose={handleClose}
            onCancel={showSaveInactive ? handleCancel : handleClose}
            onConfirm={handleConfirm}
            confirmText={getLiteral('action_automation_launch_now')}
            cancelText={
                showSaveInactive
                    ? getLiteral('action_automation_save_inactive')
                    : getLiteral('action_cancel')
            }
            title={getLiteral('label_automation_save_confirmation')}
            overrides={{
                cancelButton: {
                    type: 'secondary',
                },
            }}
        >
            <div className="fm-automation-activation-confirm-modal__gif">
                <WarningGif />
            </div>
            <Spacer y={4} />
            <Text color={theme.colors.textLight.secondary}>
                {getLiteral('label_automation_save_confirmation_desc')}
            </Text>
            <Spacer y={4} />
            <Text bold className="fm-automation-activation-confirm-modal__name">
                {name}
            </Text>
            <Spacer y={4} />
            <Markdown
                content={getLiteral('label_automation_save_confirmation_desc_2')}
                overrides={{
                    a: { target: '_blank' },
                }}
            />
            <Spacer y={2} />
        </Modal>
    );
});

AutomationActivationConfirmModal.displayName = 'AutomationActivationConfirmModal';

export default AutomationActivationConfirmModal;
